/* ============================================================================
   KYFE - FREE GIFT AUTO-ADD SCRIPT
   ----------------------------------------------------------------------------
   What it does:
     • When a customer adds a "trigger" product to their cart, the matching
       free gift(s) are automatically added.
     • If the trigger product is removed, its free gift(s) are removed too.
     • If a customer manually removes a free gift, it stays gone for that
       session (so we don't fight them by re-adding it).

   NOTE: this script only puts the gift IN the cart. What makes it FREE is the
   "Free Cold Plunge" automatic discount in Shopify Admin -> Discounts. That
   discount is scoped to the tent PRODUCTS (not variants), so it already covers
   every colour, size and bundle tier. If you add a new trigger below that is a
   different product, add it to that discount too or the gift will be charged.

   HOW TO EDIT (this is the only section you need to touch):
     Look for the "EDIT HERE" block below.
     Each line maps ONE trigger product to a LIST of free gifts.

       <TRIGGER_VARIANT_ID>: [<GIFT_VARIANT_ID>, <GIFT_VARIANT_ID>, ...]

     • To add a gift to a product:     add its variant ID inside the [ ].
     • To remove a gift:               delete that variant ID from the [ ].
     • To add a brand-new trigger:     add a new line with its gift list.
     • To stop a trigger entirely:     delete its whole line.

   Where to find a variant ID:
     Shopify Admin -> Products -> (product) -> click the variant -> the long
     number at the end of the URL is the variant ID.
   ============================================================================ */

(() => {

  /* ==========================================================================
     vvv  EDIT HERE - GIFT MAPPING  vvv
     Trigger product  ->  [ list of free gifts to auto-add ]

     Gift 48736403849513 = KYFE POD - Portable Ice Plunge
     ========================================================================== */

  const BF_BONUS_MAP = {

    // ----- LARGE TENT / STANDARD BUNDLE -----
    52928237830441: [48736403849513],   // Large / Black (Limited Edition)
    47576219582761: [48736403849513],   // Large / Forest Green
    47576219615529: [48736403849513],   // Large / Arctic Gray

    // ----- LARGE TENT / ULTIMATE BUNDLE -----
    53417638101289: [48736403849513],   // Large / Black (Limited Edition)
    53417638134057: [48736403849513],   // Large / Forest Green
    53417638166825: [48736403849513],   // Large / Arctic Gray

    // ----- MINI TENT -----
    53269880111401: [48736403849513],   // Mini / Midnight Black / Standard
    53417638199593: [48736403849513],   // Mini / Midnight Black / Ultimate

    // ----- ACCESSORY BUNDLES (both products are currently DRAFT) -----
    // Uncomment these if you set Basic / Ultimate Accessory Bundle back to
    // Active, and add those products to the Free Cold Plunge discount.
    // 49512294416681: [48736403849513],   // Basic Accessory Bundle / Standard
    // 49512294449449: [48736403849513],   // Basic Accessory Bundle / Mini
    // 49512370077993: [48736403849513],   // Ultimate Accessory Bundle / Standard
    // 49512370110761: [48736403849513],   // Ultimate Accessory Bundle / Mini

  };

  /* ==========================================================================
     ^^^  END EDIT HERE - do not change anything below unless you know JS  ^^^
     ========================================================================== */


  /* --------------------------------------------------------------------------
     Internal: build reverse lookup ( gift  ->  [triggers that grant it] )
     -------------------------------------------------------------------------- */
  const BONUS_TO_TRIGGERS = Object.entries(BF_BONUS_MAP).reduce((acc, [trigger, bonuses]) => {
    const triggerId = Number(trigger);
    (Array.isArray(bonuses) ? bonuses : [bonuses]).forEach((bonus) => {
      const bonusId = Number(bonus);
      (acc[bonusId] ||= []).push(triggerId);
    });
    return acc;
  }, {});


  /* --------------------------------------------------------------------------
     Suppression - remembers (per browser session) when a customer manually
     removed a gift, so we don't keep re-adding it against their wishes.
     -------------------------------------------------------------------------- */
  const suppressionKey   = (bonusId) => `kyfe_bonus_${bonusId}_suppressed`;
  const isGiftSuppressed = (bonusId) => sessionStorage.getItem(suppressionKey(bonusId)) === '1';
  const setGiftSuppressed = (bonusId, value) =>
    sessionStorage.setItem(suppressionKey(bonusId), value ? '1' : '0');


  /* --------------------------------------------------------------------------
     State
     -------------------------------------------------------------------------- */
  let inflight = false;   // prevents overlapping reconcile runs
  let prevCart = null;    // last known cart snapshot (for change detection)


  /* --------------------------------------------------------------------------
     Shopify cart API helpers
     -------------------------------------------------------------------------- */

  // Fetch the current cart as JSON.
  async function getCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('cart.js failed');
      return await res.json();
    } catch (e) {
      console.error('[Gift Script] getCart error:', e);
      return null;
    }
  }

  // Add a variant to the cart.
  async function addVariantToCart(variantId, qty = 1, properties = {}) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: Number(variantId), quantity: qty, properties })
      });
      if (!res.ok) throw new Error(`add.js failed for ${variantId}`);
      return await res.json();
    } catch (e) {
      console.error('[Gift Script] addVariantToCart error:', e);
      return null;
    }
  }

  // Remove every line of a given variant from the cart.
  async function removeVariantEverywhere(variantId) {
    const cart = await getCart();
    if (!cart?.items?.length) return false;

    const lineKeys = cart.items
      .filter(item => Number(item.id) === Number(variantId))
      .map(item => item.key);

    if (!lineKeys.length) return false;

    for (const key of lineKeys) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      } catch (e) {
        console.error('[Gift Script] removeVariant error:', e);
      }
    }

    return true;
  }

  // Tell the theme to refresh (and optionally open) the cart drawer.
  function themeRefresh(openDrawer = true) {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { detail: { open: !!openDrawer } })
      );
    } catch (e) {
      console.warn('[Gift Script] cart:refresh dispatch failed:', e);
    }
  }


  /* --------------------------------------------------------------------------
     Cart inspection helpers
     -------------------------------------------------------------------------- */

  // Build a Map of  variantId -> total quantity in cart.
  const qtyByVariant = (cart) => {
    const map = new Map();
    (cart?.items || []).forEach(item => {
      const id = Number(item.id);
      map.set(id, (map.get(id) || 0) + Number(item.quantity || 0));
    });
    return map;
  };

  const qtyOf = (map, id) => (map.get(Number(id)) || 0);

  const hasVariant = (cart, variantId) => qtyOf(qtyByVariant(cart), variantId) > 0;

  // Does the cart contain at least one of the given trigger variants?
  const hasAnyTrigger = (cart, triggers) =>
    (cart?.items || []).some(item => triggers.includes(Number(item.id)));

  // Which triggers grant this gift?
  const giftTriggers = (bonusId) => BONUS_TO_TRIGGERS[Number(bonusId)] || [];

  // Is this gift currently earned (i.e. is a qualifying trigger in the cart)?
  const giftEarned = (cart, bonusId) => hasAnyTrigger(cart, giftTriggers(bonusId));

  // Total quantity of all triggers that grant this gift.
  const triggerGroupQty = (cart, bonusId) => {
    const map = qtyByVariant(cart);
    return giftTriggers(bonusId).reduce((sum, id) => sum + (map.get(id) || 0), 0);
  };


  /* --------------------------------------------------------------------------
     Change detectors (compare previous cart vs current cart)
     -------------------------------------------------------------------------- */

  // Did the customer manually reduce/remove this gift while still qualifying?
  const giftManuallyRemoved = (prev, next, bonusId) => {
    if (!prev || !next) return false;
    const wentDown = qtyOf(qtyByVariant(prev), bonusId) > qtyOf(qtyByVariant(next), bonusId);
    return wentDown && giftEarned(next, bonusId);
  };

  // Did the customer add more of the trigger product(s)?
  const triggerQtyIncreased = (prev, next, bonusId) => {
    if (!prev || !next) return false;
    return triggerGroupQty(next, bonusId) > triggerGroupQty(prev, bonusId);
  };


  /* --------------------------------------------------------------------------
     Core logic: bring the cart's gifts in line with the rules
     -------------------------------------------------------------------------- */
  async function reconcileGifts(prev, next) {
    if (inflight) return;
    inflight = true;

    let cart = next || await getCart();
    if (!cart) { inflight = false; return; }

    const allGiftIds = Object.keys(BONUS_TO_TRIGGERS).map(Number);

    // 1) Update suppression flags based on what the customer just did.
    for (const bonusId of allGiftIds) {
      // Manually removed a still-qualifying gift -> respect that, suppress it.
      if (giftManuallyRemoved(prev, cart, bonusId)) {
        setGiftSuppressed(bonusId, true);
      }
      // Added more of the trigger -> treat as fresh intent, un-suppress.
      if (triggerQtyIncreased(prev, cart, bonusId)) {
        setGiftSuppressed(bonusId, false);
      }
    }

    // 2) REMOVALS - drop any gift whose trigger is no longer in the cart.
    for (const bonusId of allGiftIds) {
      const earned  = giftEarned(cart, bonusId);
      const present = hasVariant(cart, bonusId);
      if (!earned && present) {
        const removed = await removeVariantEverywhere(bonusId);
        if (removed) themeRefresh(true);
      }
    }

    // Re-fetch the cart after any removals.
    cart = await getCart();
    if (!cart) { inflight = false; return; }

    // 3) ADDITIONS - add any earned gift that's missing and not suppressed.
    for (const bonusId of allGiftIds) {
      const earned     = giftEarned(cart, bonusId);
      const present    = hasVariant(cart, bonusId);
      const suppressed = isGiftSuppressed(bonusId);

      if (earned && !present && !suppressed) {
        const added = await addVariantToCart(bonusId, 1, { _kyfe_bf_bonus: '1' });
        if (added) themeRefresh(true);
      }
    }

    inflight = false;
  }


  /* --------------------------------------------------------------------------
     Hooks - when to run the reconcile
     -------------------------------------------------------------------------- */

  // Preferred: listen to the theme's own cart update event.
  document.addEventListener('cart:updated', (ev) => {
    const next = ev?.detail?.cart || null;
    reconcileGifts(prevCart, next);
    prevCart = next || prevCart;
  });

  // Fallback: after any add-to-cart button click, re-check the cart.
  document.addEventListener('click', (e) => {
    const addButton = e.target.closest(
      'button[name="add"], button[type="submit"][form], [data-add-to-cart], [aria-label="Add to cart"]'
    );
    if (!addButton) return;

    setTimeout(async () => {
      const next = await getCart();
      await reconcileGifts(prevCart, next);
      prevCart = next || prevCart;
    }, 350);
  });

  // On page load: take a snapshot and reconcile once.
  window.addEventListener('pageshow', async () => {
    prevCart = await getCart();
    await reconcileGifts(null, prevCart);
  });

})();
